import * as Sentry from '@sentry/react';

import { ThemeContext, baseTheme } from '@kaboodle-solutions/design-system';
import store, { history } from './store';

import App from './App';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import React from 'react';
import axios from 'axios';
import { axiosInterceptors } from './lib/axios/captureException';
import constants from './constants';
import { isSentryEnabled } from './lib/logging';
import { render } from 'react-dom';
import { requestInterceptor } from './lib/axios';

const { ThemeProvider } = ThemeContext;

// Enable for reporting informative issues to Sentry dashboard.
const shouldReportInfo = false;

axios.interceptors.request.use(requestInterceptor);
axios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response ? error.response.status : null;

    if (status === 403) {
      window.location.href = constants.UNAUTHORISED_URL;
    }

    if (status === 401) {
      window.location.href = constants.LOGIN_URL;
    }
  },
);

const sentryFilter = (event, hint) => {
  const error = hint.originalException;

  if (!shouldReportInfo && event.level === 'info') {
    return null;
  }

  if (error) {
    const { code, message, status } = error;

    const networkFailErrors = ['ERR_NETWORK', 'ECONNABORTED'];

    if (code && networkFailErrors.includes(code) && status === null) {
      return null;
    }

    if (
      message &&
      (message.includes('network error') ||
        message.includes('timeout exceeded'))
    ) {
      return null;
    }
  }

  return event;
};

if (isSentryEnabled()) {
  Sentry.init({
    dsn: 'https://7fe6a341ba8d44a08bc5024986bb9e78@sentry.io/1475506',
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: sentryFilter,
    // Set tracesSampleRate to 0.05 to capture 5%
    // of transactions for performance monitoring.
    tracesSampleRate: 0.05,
    ignoreErrors: [
      // Error generated by a bug in auto-fill library on Chrome browser for IOS.
      'Blocked a frame with origin',

      // Error generated when an observed element is resized again during a single frame animation notification.
      'ResizeObserver loop completed with undelivered notifications',

      // This error means that ResizeObserver was not able to deliver all observations within a single animation frame.
      'ResizeObserver loop limit exceeded',

      'sessionstack',
      'Non-Error exception captured',

      'TypeError: Failed to fetch',
      'TypeError: NetworkError when attempting to fetch resource.',
      'TypeError: cancelled',
      'TypeError: Cancelled',
      'message: cancelled',
      'cancelled',

      'LaunchDarklyFlagFetchError',

      // Using this ignore config from https://gist.github.com/impressiver/5092952

      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Avast extension error
      '_avast_submit',
      // cross-origin error
      'Permission denied to access property',

      // iCloud password browser extension error:
      '?(FormMetadata.js)',
      "null is not an object (evaluating 'this._logicalFormForControl(e).formElement')",

      // Safari browser extension issue:
      '@webkit-masked-url(://hidden/)',
      "undefined is not an object (evaluating 'n.store')",
      'window.requestIdleCallback is not a function',

      // Range error in color module:
      'Color.prototype[model](color/index)',
    ],
    denyUrls: [
      // sessionstack
      /sessionstack/i,
      // sessionstack cdn
      /cdn\.sessionstack\.com/i,

      // TikTok blocked
      /tiktok/i,

      // Using this ignore config from https://gist.github.com/impressiver/5092952

      // Google Adsense
      /pagead\/js/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      // Cacaoweb
      /127\.0\.0\.1:4001\/isrunning/i,
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /ads\.avocet\.io/i,
    ],
  });

  // interceptor for request
  axios.interceptors.request.use(
    response => response,
    error => axiosInterceptors(error),
  );

  // interceptor for response
  axios.interceptors.response.use(
    response => response,
    error => axiosInterceptors(error),
  );
}

render(
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <ThemeProvider theme={baseTheme}>
        <App />
      </ThemeProvider>
    </ConnectedRouter>
  </Provider>,
  document.getElementById('app'),
);
